import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import axios from "axios";
import { API, CITIES } from "@/lib/api";
import { Trash2, Plus, LogOut, Edit3, X } from "lucide-react";

const STORAGE_KEY = "aqualyx_admin_token";

export default function AdminPage() {
    const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
    const [password, setPassword] = useState("");
    const [tab, setTab] = useState("clinics");
    const [clinics, setClinics] = useState([]);
    const [leads, setLeads] = useState([]);
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const headers = { headers: { "X-Admin-Password": token } };

    const login = async (e) => {
        e.preventDefault();
        try {
            const r = await axios.post(`${API}/admin/login`, { password });
            setToken(r.data.token);
            localStorage.setItem(STORAGE_KEY, r.data.token);
            toast.success("Welcome.");
        } catch {
            toast.error("Invalid password.");
        }
    };

    const logout = () => {
        localStorage.removeItem(STORAGE_KEY);
        setToken("");
    };

    const loadAll = async () => {
        setLoading(true);
        try {
            const [c, l, s] = await Promise.all([
                axios.get(`${API}/clinics`),
                axios.get(`${API}/admin/leads`, headers),
                axios.get(`${API}/admin/newsletter`, headers),
            ]);
            setClinics(c.data);
            setLeads(l.data);
            setSubs(s.data);
        } catch (e) {
            if (e?.response?.status === 401) {
                toast.error("Session expired.");
                logout();
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        if (token) loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const saveClinic = async (data) => {
        try {
            if (editing?.id) {
                await axios.put(`${API}/clinics/${editing.id}`, data, headers);
                toast.success("Clinic updated.");
            } else {
                await axios.post(`${API}/clinics`, data, headers);
                toast.success("Clinic added.");
            }
            setShowForm(false);
            setEditing(null);
            loadAll();
        } catch {
            toast.error("Save failed.");
        }
    };

    const removeClinic = async (id) => {
        if (!window.confirm("Delete this clinic?")) return;
        try {
            await axios.delete(`${API}/clinics/${id}`, headers);
            toast.success("Deleted.");
            loadAll();
        } catch {
            toast.error("Delete failed.");
        }
    };

    if (!token) {
        return (
            <div className="page-fade pt-32 md:pt-40 pb-24 min-h-screen">
                <Helmet><title>Admin — Aqualyx NZ</title><meta name="robots" content="noindex,nofollow" /></Helmet>
                <div className="max-w-md mx-auto px-6">
                    <div className="eyebrow mb-3">Administrator</div>
                    <h1 className="editorial-headline text-5xl mb-8">Sign in.</h1>
                    <form onSubmit={login} data-testid="admin-login" className="space-y-4">
                        <input
                            data-testid="admin-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Admin password"
                            className="w-full bg-transparent border border-champagne/50 px-4 py-3 focus:outline-none focus:border-champagne"
                            required
                        />
                        <button data-testid="admin-login-btn" type="submit" className="btn-primary w-full">Enter</button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="page-fade pt-32 md:pt-40 pb-24">
            <Helmet><title>Admin — Aqualyx NZ</title><meta name="robots" content="noindex,nofollow" /></Helmet>
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="eyebrow">Admin</div>
                        <h1 className="editorial-headline text-5xl md:text-6xl">Console.</h1>
                    </div>
                    <button onClick={logout} data-testid="admin-logout" className="btn-outline">
                        <LogOut size={14} /> Logout
                    </button>
                </div>

                <div className="border-b border-champagne/30 flex gap-8 mb-8">
                    {[["clinics", `Clinics (${clinics.length})`], ["leads", `Leads (${leads.length})`], ["newsletter", `Newsletter (${subs.length})`]].map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setTab(k)}
                            data-testid={`tab-${k}`}
                            className={`pb-4 eyebrow ${tab === k ? "text-champagne border-b border-champagne -mb-px" : "text-ink/50"}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {loading && <div className="eyebrow">Loading…</div>}

                {tab === "clinics" && (
                    <div>
                        <button
                            onClick={() => { setEditing(null); setShowForm(true); }}
                            data-testid="add-clinic"
                            className="btn-champagne mb-6"
                        >
                            <Plus size={14} /> Add Clinic
                        </button>
                        <div className="overflow-x-auto border border-champagne/30">
                            <table className="w-full text-sm">
                                <thead className="bg-nude">
                                    <tr className="text-left">
                                        {["Name", "City", "Phone", "Email", ""].map(h => <th key={h} className="px-4 py-3 eyebrow text-ink/60">{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {clinics.map((c) => (
                                        <tr key={c.id} className="border-t border-champagne/20" data-testid={`admin-clinic-${c.id}`}>
                                            <td className="px-4 py-3 font-serif text-base">{c.name}</td>
                                            <td className="px-4 py-3 text-ink/70">{c.city || "—"}</td>
                                            <td className="px-4 py-3 text-ink/70">{c.phone || "—"}</td>
                                            <td className="px-4 py-3 text-ink/70 break-all">{c.email || "—"}</td>
                                            <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                                                <button onClick={() => { setEditing(c); setShowForm(true); }} className="text-ink/60 hover:text-champagne" aria-label="Edit"><Edit3 size={14} /></button>
                                                <button onClick={() => removeClinic(c.id)} className="text-ink/60 hover:text-red-600" aria-label="Delete"><Trash2 size={14} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "leads" && (
                    <div className="overflow-x-auto border border-champagne/30">
                        <table className="w-full text-sm">
                            <thead className="bg-nude">
                                <tr className="text-left">
                                    {["Name", "Email", "Phone", "City", "Area", "Clinic", "Date"].map(h => <th key={h} className="px-4 py-3 eyebrow text-ink/60">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map((l) => (
                                    <tr key={l.id} className="border-t border-champagne/20">
                                        <td className="px-4 py-3 font-serif">{l.name}</td>
                                        <td className="px-4 py-3 break-all">{l.email}</td>
                                        <td className="px-4 py-3">{l.phone || "—"}</td>
                                        <td className="px-4 py-3">{l.city || "—"}</td>
                                        <td className="px-4 py-3">{l.treatment_area || "—"}</td>
                                        <td className="px-4 py-3">{l.preferred_clinic || "—"}</td>
                                        <td className="px-4 py-3 text-ink/60">{new Date(l.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "newsletter" && (
                    <div className="overflow-x-auto border border-champagne/30">
                        <table className="w-full text-sm">
                            <thead className="bg-nude">
                                <tr className="text-left">
                                    <th className="px-4 py-3 eyebrow text-ink/60">Email</th>
                                    <th className="px-4 py-3 eyebrow text-ink/60">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subs.map((s) => (
                                    <tr key={s.id} className="border-t border-champagne/20">
                                        <td className="px-4 py-3">{s.email}</td>
                                        <td className="px-4 py-3 text-ink/60">{new Date(s.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {showForm && (
                    <ClinicForm
                        initial={editing}
                        onClose={() => { setShowForm(false); setEditing(null); }}
                        onSave={saveClinic}
                    />
                )}
            </div>
        </div>
    );
}

function ClinicForm({ initial, onClose, onSave }) {
    const [data, setData] = useState({
        name: initial?.name || "",
        address: initial?.address || "",
        city: initial?.city || "",
        region: initial?.region || "New Zealand",
        website: initial?.website || "",
        email: initial?.email || "",
        phone: initial?.phone || "",
        instagram: initial?.instagram || "",
        practitioner: initial?.practitioner || "",
        certified: initial?.certified ?? true,
    });
    const change = (k) => (e) => setData({ ...data, [k]: e.target.value });

    return (
        <div className="fixed inset-0 z-[70] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-bone max-w-2xl w-full max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between p-6 border-b border-champagne/30">
                    <h3 className="font-serif text-3xl">{initial ? "Edit clinic" : "Add clinic"}</h3>
                    <button onClick={onClose} aria-label="Close"><X /></button>
                </div>
                <form
                    onSubmit={(e) => { e.preventDefault(); onSave(data); }}
                    data-testid="clinic-form"
                    className="p-6 space-y-4"
                >
                    {[
                        ["name", "Name *", true],
                        ["address", "Address"],
                        ["city", "City", false, "select"],
                        ["phone", "Phone"],
                        ["email", "Email"],
                        ["website", "Website"],
                        ["instagram", "Instagram"],
                        ["practitioner", "Practitioner"],
                    ].map(([k, label, req, type]) => (
                        <label key={k} className="block">
                            <span className="eyebrow block mb-2">{label}</span>
                            {type === "select" ? (
                                <select value={data[k]} onChange={change(k)} className="w-full border border-champagne/40 px-4 py-2.5 bg-transparent">
                                    <option value="">—</option>
                                    {CITIES.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            ) : (
                                <input
                                    required={!!req}
                                    value={data[k]}
                                    onChange={change(k)}
                                    className="w-full border border-champagne/40 px-4 py-2.5 bg-transparent focus:outline-none focus:border-champagne"
                                    data-testid={`clinic-form-${k}`}
                                />
                            )}
                        </label>
                    ))}
                    <div className="flex gap-3 justify-end pt-4">
                        <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
                        <button type="submit" className="btn-primary" data-testid="clinic-form-save">Save</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
